import type { ReactNode } from 'react';
import type { SystemReport } from '@roomtreatment/av-compat';
import {
  CONTENT,
  CONTENT_NAME,
  EXPLANATION,
  READOUT_LABEL,
  READOUT_TONE,
  READOUT_VALUE,
  SECOND_LINE,
} from '@/components/seo/roles';
import { headroomReadouts, verdictCopy, type VerdictTone } from './checkerCopy';

// Status hues belong to a readout reporting an outcome (AGENTS.md
// status-readout rule): the verdict, and the per-metric readouts below it.
// The hue classes, and why they carry `!`, live with the roles.
const TONE: Record<VerdictTone, string> = { ...READOUT_TONE, neutral: '' };

function Block({ title, children, testId }: { title: string; children: ReactNode; testId?: string }) {
  return (
    <div className="space-y-2" data-testid={testId}>
      <h3 className={CONTENT_NAME}>{title}</h3>
      {children}
    </div>
  );
}

/** The verdict, issues, unknowns and headroom for one checked system. `actions` sits under the room checks. */
export default function CheckerResults({ report, actions }: { report: SystemReport; actions: ReactNode }) {
  const verdict = verdictCopy(report);
  const readouts = headroomReadouts(report);

  return (
    <div className="space-y-6" aria-live="polite">
      <div className="space-y-1" data-testid="checker-verdict" data-verdict={report.verdict}>
        <div className={READOUT_LABEL}>Result</div>
        <div className={`${READOUT_VALUE} ${TONE[verdict.tone]}`}>{verdict.title}</div>
        <p className={SECOND_LINE}>{verdict.detail}</p>
      </div>

      {report.issues.length > 0 && (
        <Block title="Problems" testId="checker-issues">
          <ul className="space-y-3">
            {report.issues.map((issue) => (
              <li key={issue.id} className="space-y-1">
                <p className={CONTENT}>{issue.text}</p>
                <p className={SECOND_LINE}>{issue.fix}</p>
              </li>
            ))}
          </ul>
        </Block>
      )}

      {report.unknown.length > 0 && (
        <Block title="Couldn’t check" testId="checker-unknown">
          <ul className="space-y-3">
            {report.unknown.map((u) => (
              <li key={`${u.rule}:${u.productId}`} className={CONTENT}>{u.text}</li>
            ))}
          </ul>
        </Block>
      )}

      {readouts && (
        <Block title="Receiver and speakers" testId="checker-headroom">
          <dl className="grid grid-cols-2 gap-6">
            {readouts.map((r) => (
              <div key={r.label} className="space-y-1">
                <dt className={READOUT_LABEL}>{r.label}</dt>
                <dd className={`${READOUT_VALUE} ${r.tone ? READOUT_TONE[r.tone] : ''}`}>{r.value}</dd>
                <dd className={SECOND_LINE}>{r.note}</dd>
              </div>
            ))}
          </dl>
        </Block>
      )}

      <Block title="Check these in your room" testId="checker-room-checks">
        <p className={EXPLANATION}>
          These depend on where everything sits, so they run on your real room in the 3D editor.
        </p>
        {report.roomChecks.length > 0 && (
          <ul className={`list-disc space-y-1 pl-5 ${CONTENT}`}>
            {report.roomChecks.map((c) => (
              <li key={c.rule}>{c.label}</li>
            ))}
          </ul>
        )}
        <div className="flex flex-wrap items-center gap-3 pt-2">{actions}</div>
      </Block>
    </div>
  );
}
